"use client";

import { useState, useRef, useMemo, useEffect } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  EyeOff,
  Check,
  ChevronLeft,
  ChevronRight,
  Mail,
  Calendar,
  Globe,
  Camera,
  Lock,
  Plus,
  Trash2,
  Shield,
  Users,
  Trophy,
  Clipboard,
  Star,
  BookOpen,
  TrendingUp,
  ClipboardList,
} from "lucide-react";
import { COUNTRIES, countryCodeToFlag } from "../countries";
import { useLang } from "@/lib/i18n/LangProvider";
import { syncCoachProfileToBackend } from "@/lib/api/auth";
import { acceptTeamInvite, previewTeamInvite, type TeamInvitePreview } from "@/lib/api/coach";
import { fileToAvatarDataUrl } from "@/lib/image";
import {
  setSavedCoachProfile,
  setSignedInCoach,
  type CoachUser,
  type CoachingRole,
  type CoachingLicense,
  type AgeGroup,
  type SquadLevel,
  type CoachTeamEntry,
  LICENSE_RANK,
} from "@/lib/coach-user";
import { BrandMark } from "@/components/BrandMark";

// ── Steps ────────────────────────────────────────────────────────────────────

// The base flow. An INVITED coach walks a shorter one — see `STEPS` inside the
// component: the "club" step exists to create a team, and someone joining a team
// that already exists must not be asked to create another.
const ALL_STEPS = [
  { id: "role",       title: "Coaching role"   },
  { id: "name",       title: "Your name"       },
  { id: "dob",        title: "Date of birth"   },
  { id: "nationality",title: "Nationality"     },
  { id: "license",    title: "Coaching licence"},
  { id: "experience", title: "Experience"      },
  { id: "club",       title: "Current club"    },
  { id: "history",    title: "Club history"    },
  { id: "photo",      title: "Profile picture" },
  { id: "account",    title: "Account"         },
] as const;

// ── Animations ───────────────────────────────────────────────────────────────

const slideLeft  = { initial: { opacity: 0, x: -18 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: 18 },  transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] as [number,number,number,number] } };
const slideRight = { initial: { opacity: 0, x: 18  }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -18 }, transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] as [number,number,number,number] } };

// ── Coaching role options with icons ─────────────────────────────────────────

const ROLE_OPTIONS: { id: CoachingRole; icon: React.ReactNode; descKey: string }[] = [
  { id: "head_coach",         icon: <Trophy className="w-6 h-6" />,   descKey: "coachSignup.roleDesc.head_coach"         },
  { id: "assistant_coach",    icon: <Clipboard className="w-6 h-6" />, descKey: "coachSignup.roleDesc.assistant_coach"   },
  { id: "gk_coach",           icon: <Shield className="w-6 h-6" />,   descKey: "coachSignup.roleDesc.gk_coach"           },
  { id: "fitness_coach",      icon: <TrendingUp className="w-6 h-6" />, descKey: "coachSignup.roleDesc.fitness_coach"     },
  { id: "technical_director", icon: <Star className="w-6 h-6" />,     descKey: "coachSignup.roleDesc.technical_director" },
  { id: "scout",              icon: <BookOpen className="w-6 h-6" />, descKey: "coachSignup.roleDesc.scout"              },
  { id: "academy_coach",      icon: <Users className="w-6 h-6" />,    descKey: "coachSignup.roleDesc.academy_coach"      },
  { id: "team_manager",       icon: <ClipboardList className="w-6 h-6" />, descKey: "coachSignup.roleDesc.team_manager"  },
];

// Grouped license sections for the signup UI
const LICENSE_GROUPS: { labelKey: string; flag: string; options: CoachingLicense[] }[] = [
  { labelKey: "coachSignup.licenseGroup.entry",    flag: "⬜", options: ["none", "grassroots"] },
  { labelKey: "coachSignup.licenseGroup.uefa",     flag: "🇪🇺", options: ["uefa_c", "uefa_b", "uefa_a", "uefa_pro"] },
  { labelKey: "coachSignup.licenseGroup.ussf",     flag: "🇺🇸", options: ["ussf_d", "ussf_c", "ussf_b", "ussf_a", "ussf_pro"] },
  { labelKey: "coachSignup.licenseGroup.concacaf", flag: "🌎", options: ["concacaf_b", "concacaf_a"] },
  { labelKey: "coachSignup.licenseGroup.afc",      flag: "🌏", options: ["afc_a"] },
  { labelKey: "coachSignup.licenseGroup.caf",      flag: "🌍", options: ["caf_a"] },
  { labelKey: "coachSignup.licenseGroup.other",    flag: "🏆", options: ["nscaa_national", "other"] },
];

const LICENSE_OPTIONS: CoachingLicense[] = LICENSE_GROUPS.flatMap(g => g.options);

const AGE_GROUP_OPTIONS: AgeGroup[] = [
  "u6_u9", "u10_u12", "u13_u16", "u17_u21",
  "senior_amateur", "senior_semi_pro", "senior_professional", "womens", "futsal",
];

const SQUAD_LEVELS: SquadLevel[] = ["amateur", "semi_pro", "professional", "academy", "national_team"];

// ── Tactical dot positions (mini pitch visual) ────────────────────────────────

const TACTIC_DOTS = [
  { x: 50, y: 90 },                           // GK
  { x: 18, y: 70 }, { x: 38, y: 72 }, { x: 62, y: 72 }, { x: 82, y: 70 }, // Def
  { x: 25, y: 48 }, { x: 50, y: 44 }, { x: 75, y: 48 },                    // Mid
  { x: 30, y: 22 }, { x: 50, y: 18 }, { x: 70, y: 22 },                    // Att
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function CoachSignUpPage() {
  const { t, lang, setLang }                = useLang();
  const MONTHS = lang === "es"
    ? ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"]
    : ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const [step, setStep]                     = useState(0);
  const [dir,  setDir]                      = useState<1 | -1>(1);

  // ── Invited coach (?invite=<token>) ────────────────────────────────────────
  // Read from window.location rather than useSearchParams: this page is a
  // client component with no Suspense boundary, and useSearchParams would opt
  // the whole route out of static rendering at build time.
  const [inviteToken, setInviteToken]       = useState<string | null>(null);
  const [invite, setInvite]                 = useState<TeamInvitePreview | null>(null);
  const [inviteWarning, setInviteWarning]   = useState<string | null>(null);

  useEffect(() => {
    let token: string | null = null;
    try { token = new URLSearchParams(window.location.search).get("invite"); }
    catch { token = null; }
    if (!token) return;
    setInviteToken(token);
    previewTeamInvite(token)
      // Only a LIVE invite shortens the flow. A dead one must not silently
      // leave the coach with an account and no team — he still creates his own.
      .then((p) => { if (p.state === "live") setInvite(p); })
      .catch(() => { /* the invite page itself explains a dead link */ });
  }, []);

  // The invited coach is joining a team that already exists, so he is never
  // asked to create one. Set on mount, before any step is taken, so the index
  // the user is on cannot drift when the list shortens.
  const STEPS = useMemo(
    () => (invite ? ALL_STEPS.filter((s) => s.id !== "club") : ALL_STEPS),
    [invite],
  );

  // Step 1 — role
  const [coachingRole, setCoachingRole]     = useState<CoachingRole | null>(null);

  // Step 2 — name
  const [firstName, setFirstName]           = useState("");
  const [lastName,  setLastName]            = useState("");

  // Step 3 — dob
  const [dob, setDob]                       = useState({ year: 1985, month: 6, day: 15 });

  // Step 4 — nationality
  const [nationality, setNationality]       = useState<string | null>(null);
  const [natSearch,   setNatSearch]         = useState("");

  // Step 5 — license
  const [license, setLicense]               = useState<CoachingLicense | null>(null);

  // Step 6 — experience
  const [yearsExp, setYearsExp]             = useState(5);
  const [ageGroups, setAgeGroups]           = useState<AgeGroup[]>([]);

  // Step 7 — current club
  const [hasClub, setHasClub]               = useState<boolean | null>(null);
  const [clubName, setClubName]             = useState("");
  const [clubCountry, setClubCountry]       = useState("");
  const [clubCountryOpen, setClubCountryOpen] = useState(false);
  const [clubCountrySearch, setClubCountrySearch] = useState("");
  const [squadLevel, setSquadLevel]         = useState<SquadLevel | null>(null);

  // Step 8 — history
  const [teamHistory, setTeamHistory]       = useState<CoachTeamEntry[]>([
    { country: "", club: "", current: false, dateFrom: "", dateTo: "", role: "" },
  ]);
  const [histCountryOpen, setHistCountryOpen] = useState<number | null>(null);
  const [histCountrySearch, setHistCountrySearch] = useState("");

  // Step 9 — photo
  const [profileImage,   setProfileImage]   = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Step 10 — account
  const [email,           setEmail]         = useState("");
  const [password,        setPassword]      = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass,        setShowPass]      = useState(false);

  const [submitting, setSubmitting]         = useState(false);
  const [error, setError]                   = useState("");
  const [success,    setSuccess]            = useState(false);

  // ── Computed ───────────────────────────────────────────────────────────────

  const filteredNat = useMemo(() => {
    const q = natSearch.toLowerCase();
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 12);
  }, [natSearch]);

  const filteredClubCountry = useMemo(() => {
    const q = clubCountrySearch.toLowerCase().trim();
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q));
  }, [clubCountrySearch]);

  const filteredHistCountry = useMemo(() => {
    const q = histCountrySearch.toLowerCase().trim();
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q));
  }, [histCountrySearch]);

  const licenseRank = license ? LICENSE_RANK[license] : 0;

  // ── Validation ─────────────────────────────────────────────────────────────

  const canNext = () => {
    const id = STEPS[step].id;
    switch (id) {
      case "role":        return coachingRole !== null;
      case "name":        return firstName.trim().length > 0 && lastName.trim().length > 0;
      case "dob":         return true;
      case "nationality": return nationality !== null;
      case "license":     return license !== null;
      case "experience":  return ageGroups.length > 0;
      case "club":        return hasClub !== null && (hasClub === false || (clubName.trim().length > 0 && squadLevel !== null));
      case "history":     return true;
      case "photo":       return true;
      case "account":     return email.length > 0 && password.length >= 8 && confirmPassword === password;
      default:            return false;
    }
  };

  // ── Navigation ─────────────────────────────────────────────────────────────

  const goNext = () => {
    if (step < STEPS.length - 1) { setDir(1); setStep((s) => s + 1); }
  };
  const goBack = () => {
    if (step > 0) { setDir(-1); setStep((s) => s - 1); }
  };

  // ── History helpers ────────────────────────────────────────────────────────

  const addHistory = () =>
    setTeamHistory((t) => [...t, { country: "", club: "", current: false, dateFrom: "", dateTo: "", role: "" }]);
  const removeHistory = (i: number) => setTeamHistory((t) => t.filter((_, idx) => idx !== i));
  const updateHistory = (i: number, field: keyof CoachTeamEntry, value: string | boolean) =>
    setTeamHistory((t) => t.map((e, idx) => (idx === i ? { ...e, [field]: value } : e)));

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < STEPS.length - 1) { goNext(); return; }
    setError("");   // clear any prior failure before this attempt
    setSubmitting(true);

    // Downscale to a ~256px thumbnail. A full-resolution phone photo carried as a
    // base64 data URL is 3–10 MB and overflowed the storage quota → the crash the
    // coach hit right after the photo step. A JPEG thumbnail is ~20–40 KB.
    const profilePhoto = profileImage ? await fileToAvatarDataUrl(profileImage) : null;

    const coach: CoachUser = {
      userType: "coach",
      firstName,
      lastName,
      gender: null,
      email,
      nationality,
      dob,
      coachingRole,
      coachingLicense: license,
      yearsExperience: yearsExp,
      ageGroups,
      currentClub: hasClub && clubName.trim()
        ? { name: clubName.trim(), country: clubCountry, squadLevel: squadLevel! }
        : null,
      teamHistory,
      bio: "",
      profilePhoto,
    };

    // ── Create Firebase account ─────────────────────────────────────────
    try {
      const { createUserWithEmailAndPassword, sendEmailVerification } = await import("firebase/auth");
      const { getFirebaseAuth } = await import("@/lib/firebase");
      const auth = getFirebaseAuth();
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      if (credential.user) {
        sendEmailVerification(credential.user).catch(() => {});
      }
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? "";
      if (code === "auth/email-already-in-use") {
        setError(t("signup.errEmailInUse"));
      } else if (code === "auth/weak-password") {
        setError(t("signup.errWeakPassword"));
      } else {
        setError(t("signup.errCreateFailed"));
      }
      setSubmitting(false);
      return;
    }

    // Firebase user created — tokens now available for backend sync.
    // The Firebase account already exists at this point — a local-persist or
    // backend-sync hiccup must NOT strand the coach on the form. Advance to the
    // success screen regardless (storage writes are quota-hardened; sync retries
    // on next load).
    try {
      setSavedCoachProfile(coach);
      setSignedInCoach(coach);
      await syncCoachProfileToBackend(coach);
    } catch {
      /* non-fatal */
    }

    // Redeem the invite LAST, and only after the profile sync: accepting needs
    // a coach account, and syncCoachProfileToBackend is what sets user_role.
    // A failure here is NOT silent — the account exists either way, and the
    // coach has to be told the team was not joined rather than discover an
    // empty dashboard. The link is single-use only on SUCCESS, so re-opening it
    // still works.
    if (inviteToken && invite) {
      try {
        await acceptTeamInvite(inviteToken);
      } catch (e) {
        setInviteWarning((e as { message?: string })?.message
          ?? t("coachSignup.inviteFailed"));
      }
    }

    setSuccess(true);
    setSubmitting(false);
  };

  // ── Success screen ─────────────────────────────────────────────────────────

  if (success) {
    return (
      <div className="min-h-screen bg-ambient flex flex-col">
        <header className="fixed top-0 left-0 right-0 z-50 px-4 py-3 md:px-8" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
          <div className="mx-auto max-w-4xl flex justify-center">
            <Link href="/" className="flex items-center gap-2 text-white/90 hover:text-white transition-colors">
              <BrandMark className="h-7 w-7 rounded-lg" />
              <span className="font-display text-base font-semibold tracking-tight">Draffte</span>
            </Link>
          </div>
        </header>
        <motion.div
          className="flex-1 flex flex-col items-center justify-center px-6 py-24"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          {/* Animated dots background */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full bg-violet-500/40"
                style={{ left: `${15 + i * 14}%`, top: `${30 + (i % 3) * 15}%` }}
                animate={{ y: [-6, 6, -6], opacity: [0.3, 0.7, 0.3] }}
                transition={{ duration: 2.5 + i * 0.4, repeat: Infinity, delay: i * 0.3 }}
              />
            ))}
          </div>

          <motion.div
            className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 via-violet-600 to-emerald-600 flex items-center justify-center mb-6 shadow-[0_0_60px_rgba(139,92,246,0.4)]"
            initial={{ scale: 0, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 18, delay: 0.1 }}
          >
            <Check className="w-10 h-10 text-white" strokeWidth={2.5} />
          </motion.div>

          <motion.h1
            className="font-display text-2xl sm:text-3xl font-semibold text-white text-center mb-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {t("coachSignup.welcome").replace("{name}", firstName)}
          </motion.h1>
          <motion.p
            className="text-zinc-400 text-center max-w-sm text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45 }}
          >
            {invite ? t("coachSignup.invitedSuccessSub").replace("{team}", invite.team_name ?? "")
                    : t("coachSignup.successSub")}
          </motion.p>
          {inviteWarning && (
            /* The captured reason, not a constant. The likeliest failure here is
               signing up with an address the invite was not sent to, and the
               backend answers that with the one sentence that resolves it —
               naming the invited address. Replacing it with "open the link
               again once you're signed in" sends the coach round a loop that
               403s identically every time, with no hint why. */
            <p className="mt-3 max-w-sm text-center text-xs leading-relaxed text-amber-300">
              {inviteWarning}
            </p>
          )}

          <motion.div
            className="mt-8 flex flex-wrap items-center justify-center gap-3"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            {/* Hard navigation (plain <a>, not <Link>): right after sign-up a
                client-side route transition can request a JS chunk whose hash
                changed under a fresh deploy → "client-side exception during
                loading". A full page load always pulls fresh HTML + chunks. */}
            <a
              href="/coach-home"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-violet-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_0_24px_rgba(139,92,246,0.35)] hover:from-violet-400 hover:to-violet-500 transition-all"
            >
              {t("coachSignup.goToDashboard")} <ChevronRight className="w-4 h-4" />
            </a>
            <Link
              href="/"
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 border border-white/20 px-6 py-3 text-sm font-medium text-white hover:bg-white/15 transition-colors"
            >
              {t("signup.backToHome")}
            </Link>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  const stepId  = STEPS[step].id;
  const isLast  = step === STEPS.length - 1;
  const motionProps = dir === 1 ? slideLeft : slideRight;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-ambient flex flex-col">
      {/* Ambient blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-violet-500/12 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-indigo-500/08 rounded-full blur-[90px]" />
        <div className="absolute top-1/2 left-0 w-[300px] h-[300px] bg-violet-700/06 rounded-full blur-[80px]" />
      </div>

      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 px-4 py-2.5 md:px-8 border-b border-white/[0.06] bg-[#050508]/80 backdrop-blur-xl"
        style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}
      >
        <div className="mx-auto max-w-6xl flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 text-white/90 hover:text-white transition-colors">
            <BrandMark className="h-7 w-7 rounded-lg" />
            <span className="font-display text-sm font-semibold tracking-tight hidden sm:inline">Draffte</span>
          </Link>
          <div className="flex items-center gap-3">
            {/* Compact language toggle */}
            <div className="inline-flex items-center gap-0.5 rounded-lg border border-white/10 bg-white/5 p-0.5">
              {([["en", "English", "🇺🇸"], ["es", "Español", "🇪🇸"]] as const).map(([code, label, flag]) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setLang(code)}
                  aria-label={label}
                  className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                    lang === code
                      ? "bg-violet-500/25 text-white"
                      : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <span className="text-sm leading-none">{flag}</span>
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
            <span className="text-xs text-zinc-500">{t("coachSignup.stepWord")} {step + 1}/{STEPS.length}</span>
            <div className="flex gap-1">
              {STEPS.map((_, i) => (
                <motion.div
                  key={i}
                  className={`h-1 rounded-full transition-all duration-500 ${i < step ? "bg-violet-500/70" : i === step ? "bg-violet-400" : "bg-white/10"}`}
                  animate={{ width: i === step ? 16 : 6 }}
                  transition={{ duration: 0.3 }}
                />
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Two-column layout */}
      <main
        className="relative flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,440px)_1fr] gap-8 lg:gap-12 xl:gap-16 items-start lg:items-center px-4 md:px-8 pb-32 max-w-6xl mx-auto w-full"
        style={{ paddingTop: "calc(4.5rem + env(safe-area-inset-top))" }}
      >

        {/* ── LEFT: Form ── */}
        <div className="lg:py-8">
          {/* An invited coach is told what he is joining, on every step — and
              the flow never asks him to create a team, because he is not. */}
          {invite && (
            <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-violet-500/25 bg-violet-500/[0.08] px-3.5 py-3">
              <Shield className="mt-0.5 h-4 w-4 shrink-0 text-violet-300" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white">
                  {t("coachSignup.invitedTo").replace("{team}", invite.team_name ?? "")}
                </p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-zinc-400">
                  {invite.club_name
                    ? t("coachSignup.invitedNoteClub").replace("{club}", invite.club_name)
                    : t("coachSignup.invitedNote")}
                </p>
              </div>
            </div>
          )}
          <AnimatePresence mode="wait">

            {/* STEP 1 — Coaching role */}
            {stepId === "role" && (
              <motion.div key="role" className="space-y-6" {...motionProps}>
                <div>
                  <p className="text-xs font-semibold tracking-widest text-violet-400/80 uppercase mb-1">{t("coachSignup.eyebrow")}</p>
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-1">{t("coachSignup.roleTitle")}</h2>
                  <p className="text-zinc-400 text-sm">{t("coachSignup.roleSub")}</p>
                </div>
                <div className="space-y-2">
                  {ROLE_OPTIONS.map((opt) => {
                    const sel = coachingRole === opt.id;
                    return (
                      <motion.button
                        key={opt.id}
                        type="button"
                        onClick={() => setCoachingRole(opt.id)}
                        className={`w-full flex items-center gap-4 rounded-xl border-2 px-4 py-3.5 text-left transition-all ${sel ? "border-violet-500 bg-violet-500/12" : "border-white/8 bg-white/3 hover:border-white/15 hover:bg-white/5"}`}
                        whileHover={{ scale: 1.005, x: 2 }}
                        whileTap={{ scale: 0.995 }}
                      >
                        <span className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${sel ? "bg-violet-500/20 text-violet-300" : "bg-white/5 text-zinc-500"}`}>
                          {opt.icon}
                        </span>
                        <div className="flex-1 min-w-0">
                          <span className={`block text-sm font-semibold ${sel ? "text-white" : "text-zinc-300"}`}>
                            {t(`coachSignup.role.${opt.id}`)}
                          </span>
                          <span className="block text-xs text-zinc-500 truncate">{t(opt.descKey)}</span>
                        </div>
                        {sel && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="shrink-0 w-5 h-5 rounded-full bg-violet-500 flex items-center justify-center"
                          >
                            <Check className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* STEP 2 — Name */}
            {stepId === "name" && (
              <motion.div key="name" className="space-y-6" {...motionProps}>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-1">{t("coachSignup.nameTitle")}</h2>
                  <p className="text-zinc-400 text-sm">{t("coachSignup.nameSub")}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-zinc-400">{t("signup.firstName")}</label>
                    <input
                      autoFocus
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder={t("signup.firstNamePh")}
                      className="w-full rounded-xl border border-white/10 bg-white/5 py-3 px-4 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-zinc-400">{t("signup.lastName")}</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder={t("signup.lastNamePh")}
                      className="w-full rounded-xl border border-white/10 bg-white/5 py-3 px-4 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 3 — Date of birth */}
            {stepId === "dob" && (
              <motion.div key="dob" className="space-y-6" {...motionProps}>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-1">{t("signup.dobTitle")}</h2>
                  <p className="text-zinc-400 text-sm">{t("coachSignup.dobSub")}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-4 flex gap-3 justify-between">
                  {/* Day */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-zinc-500">{t("signup.dobDay")}</label>
                    <select value={dob.day} onChange={(e) => setDob((d) => ({ ...d, day: Number(e.target.value) }))} className="bg-transparent text-white text-sm font-semibold outline-none cursor-pointer">
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={d} className="bg-zinc-900">{d}</option>
                      ))}
                    </select>
                  </div>
                  <div className="w-px bg-white/10" />
                  {/* Month */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-zinc-500">{t("signup.dobMonth")}</label>
                    <select value={dob.month} onChange={(e) => setDob((d) => ({ ...d, month: Number(e.target.value) }))} className="bg-transparent text-white text-sm font-semibold outline-none cursor-pointer">
                      {MONTHS.map((m, i) => (
                        <option key={m} value={i + 1} className="bg-zinc-900">{m}</option>
                      ))}
                    </select>
                  </div>
                  <div className="w-px bg-white/10" />
                  {/* Year */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-zinc-500">{t("signup.dobYear")}</label>
                    <select value={dob.year} onChange={(e) => setDob((d) => ({ ...d, year: Number(e.target.value) }))} className="bg-transparent text-white text-sm font-semibold outline-none cursor-pointer">
                      {Array.from({ length: 60 }, (_, i) => 1964 + i).map((y) => (
                        <option key={y} value={y} className="bg-zinc-900">{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 4 — Nationality */}
            {stepId === "nationality" && (
              <motion.div key="nationality" className="space-y-6" {...motionProps}>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-1">{t("signup.nationalityTitle")}</h2>
                  <p className="text-zinc-400 text-sm">{t("coachSignup.natSub")}</p>
                </div>
                <input
                  type="text"
                  value={natSearch}
                  onChange={(e) => setNatSearch(e.target.value)}
                  placeholder={t("signup.searchCountry")}
                  className="w-full rounded-xl border border-white/10 bg-white/5 py-3 px-4 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
                />
                <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1 team-list-scroll">
                  {filteredNat.map((c) => {
                    const sel = nationality === c.code;
                    return (
                      <motion.button
                        key={c.code}
                        type="button"
                        onClick={() => { setNationality(c.code); setNatSearch(""); }}
                        className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left transition-all border ${sel ? "border-violet-500 bg-violet-500/15" : "border-white/8 bg-white/3 hover:bg-white/7 hover:border-white/12"}`}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="text-xl leading-none">{countryCodeToFlag(c.code)}</span>
                        <span className={`text-sm truncate ${sel ? "text-white font-medium" : "text-zinc-300"}`}>{c.name}</span>
                        {sel && <Check className="w-3.5 h-3.5 text-violet-400 ml-auto shrink-0" />}
                      </motion.button>
                    );
                  })}
                </div>
                {nationality && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 text-sm text-zinc-400">
                    <span className="text-base">{countryCodeToFlag(nationality)}</span>
                    <span>{COUNTRIES.find((c) => c.code === nationality)?.name}</span>
                    <button type="button" onClick={() => setNationality(null)} className="ml-auto text-xs text-zinc-500 hover:text-zinc-300 transition-colors">{t("coachSignup.change")}</button>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* STEP 5 — License */}
            {stepId === "license" && (
              <motion.div key="license" className="space-y-6" {...motionProps}>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-1">{t("coachSignup.licenseTitle")}</h2>
                  <p className="text-zinc-400 text-sm">{t("coachSignup.licenseSub")}</p>
                </div>
                <div className="space-y-4">
                  {LICENSE_GROUPS.map(({ labelKey, flag, options }) => (
                    <div key={labelKey}>
                      {/* Section header */}
                      <div className="flex items-center gap-2 mb-2 px-1">
                        <span className="text-sm">{flag}</span>
                        <span className="text-xs font-semibold text-zinc-500 tracking-wide uppercase">{t(labelKey)}</span>
                        <div className="flex-1 h-px bg-white/[0.05]" />
                      </div>
                      <div className="space-y-1.5">
                        {options.map((lic) => {
                          const sel = license === lic;
                          const rank = LICENSE_RANK[lic];
                          const glowColors = ["", "rgba(255,255,255,0.1)", "rgba(139,92,246,0.2)", "rgba(139,92,246,0.3)", "rgba(139,92,246,0.45)", "rgba(251,191,36,0.35)"];
                          return (
                            <motion.button
                              key={lic}
                              type="button"
                              onClick={() => setLicense(lic)}
                              className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${sel ? "border-violet-500 bg-violet-500/12" : "border-white/8 bg-white/3 hover:border-white/15"}`}
                              style={sel ? { boxShadow: `0 0 20px ${glowColors[rank]}` } : {}}
                              whileHover={{ scale: 1.005, x: 2 }}
                              whileTap={{ scale: 0.995 }}
                            >
                              {/* Rank pips */}
                              <div className="flex gap-0.5 shrink-0">
                                {[1, 2, 3, 4, 5].map((r) => (
                                  <div
                                    key={r}
                                    className={`w-1.5 h-4 rounded-sm transition-colors ${r <= rank ? (rank === 5 ? "bg-amber-400" : "bg-violet-500") : "bg-white/10"}`}
                                  />
                                ))}
                              </div>
                              <span className={`flex-1 text-sm font-medium ${sel ? "text-white" : "text-zinc-300"}`}>
                                {t(`coachSignup.license.${lic}`)}
                              </span>
                              {sel && (
                                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-5 h-5 rounded-full bg-violet-500 flex items-center justify-center shrink-0">
                                  <Check className="w-3 h-3 text-white" />
                                </motion.div>
                              )}
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* STEP 6 — Experience */}
            {stepId === "experience" && (
              <motion.div key="experience" className="space-y-6" {...motionProps}>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-1">{t("coachSignup.experienceTitle")}</h2>
                  <p className="text-zinc-400 text-sm">{t("coachSignup.experienceSub")}</p>
                </div>

                {/* Years slider */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-zinc-400">{t("coachSignup.yearsExp")}</label>
                    <span className="font-display text-xl font-bold text-white tabular-nums">
                      {yearsExp === 30 ? "30+" : yearsExp}<span className="text-sm font-normal text-zinc-400 ml-1">{t("coachSignup.yearsUnit")}</span>
                    </span>
                  </div>
                  <input
                    type="range" min={0} max={30} value={yearsExp}
                    onChange={(e) => setYearsExp(Number(e.target.value))}
                    className="w-full h-2 appearance-none bg-white/10 rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-violet-500 [&::-webkit-slider-thumb]:shadow-[0_0_16px_rgba(139,92,246,0.5)] [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-zinc-600">
                    <span>0</span><span>5</span><span>10</span><span>15</span><span>20</span><span>25</span><span>30+</span>
                  </div>
                </div>

                {/* Age groups */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-zinc-400">{t("coachSignup.ageGroupsLabel")} <span className="text-violet-400">*</span></label>
                  <div className="grid grid-cols-2 gap-2">
                    {AGE_GROUP_OPTIONS.map((ag) => {
                      const sel = ageGroups.includes(ag);
                      return (
                        <motion.button
                          key={ag}
                          type="button"
                          onClick={() =>
                            setAgeGroups((prev) =>
                              sel ? prev.filter((x) => x !== ag) : [...prev, ag]
                            )
                          }
                          className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-left border-2 transition-all ${sel ? "border-violet-500 bg-violet-500/12 text-white" : "border-white/8 bg-white/3 text-zinc-400 hover:border-white/15"}`}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${sel ? "border-violet-500 bg-violet-500" : "border-white/20"}`}>
                            {sel && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          {t(`coachSignup.age.${ag}`)}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 7 — Current club */}
            {stepId === "club" && (
              <motion.div key="club" className="space-y-6" {...motionProps}>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-1">{t("coachSignup.clubTitle")}</h2>
                  <p className="text-zinc-400 text-sm">{t("coachSignup.clubSub")}</p>
                </div>

                {/* Yes / No */}
                <div className="grid grid-cols-2 gap-3">
                  {[{ val: true, label: t("coachSignup.clubYes") }, { val: false, label: t("coachSignup.clubNo") }].map((opt) => {
                    const sel = hasClub === opt.val;
                    return (
                      <motion.button
                        key={String(opt.val)}
                        type="button"
                        onClick={() => setHasClub(opt.val)}
                        className={`rounded-xl border-2 py-4 flex flex-col items-center gap-1.5 transition-all ${sel ? "border-violet-500 bg-violet-500/15" : "border-white/10 bg-white/5 hover:border-white/20"}`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className={`text-sm font-semibold ${sel ? "text-white" : "text-zinc-400"}`}>{opt.label}</span>
                      </motion.button>
                    );
                  })}
                </div>

                {/* Club details */}
                <AnimatePresence>
                  {hasClub === true && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-4 overflow-hidden"
                    >
                      <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-zinc-400">{t("coachSignup.clubNameLabel")}</label>
                        <input
                          type="text"
                          value={clubName}
                          onChange={(e) => setClubName(e.target.value)}
                          placeholder={t("coachSignup.clubNamePh")}
                          className="w-full rounded-xl border border-white/10 bg-white/5 py-3 px-4 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
                        />
                      </div>

                      {/* Country */}
                      <div className="relative space-y-1.5">
                        <label className="block text-sm font-medium text-zinc-400">{t("coachSignup.clubCountry")}</label>
                        <button
                          type="button"
                          onClick={() => setClubCountryOpen((o) => !o)}
                          className="w-full flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 py-3 px-4 text-left transition-all hover:border-white/20"
                        >
                          {clubCountry ? (
                            <>
                              <span className="text-base">{countryCodeToFlag(clubCountry)}</span>
                              <span className="text-white text-sm">{COUNTRIES.find((c) => c.code === clubCountry)?.name}</span>
                            </>
                          ) : (
                            <span className="text-zinc-500 text-sm">{t("signup.selectCountry")}</span>
                          )}
                          <Globe className="w-4 h-4 text-zinc-500 ml-auto" />
                        </button>
                        <AnimatePresence>
                          {clubCountryOpen && (
                            <motion.div
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              className="absolute z-20 top-full mt-1 left-0 right-0 rounded-xl border border-white/10 bg-zinc-900/95 backdrop-blur-xl shadow-2xl overflow-hidden"
                            >
                              <div className="p-2 border-b border-white/5">
                                <input
                                  autoFocus
                                  type="text"
                                  value={clubCountrySearch}
                                  onChange={(e) => setClubCountrySearch(e.target.value)}
                                  placeholder={t("coachSignup.searchShort")}
                                  className="w-full bg-white/5 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 outline-none"
                                />
                              </div>
                              <div className="max-h-48 overflow-y-auto team-list-scroll">
                                {filteredClubCountry.slice(0, 20).map((c) => (
                                  <button
                                    key={c.code}
                                    type="button"
                                    onClick={() => { setClubCountry(c.code); setClubCountryOpen(false); setClubCountrySearch(""); }}
                                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-zinc-300 hover:bg-white/5 hover:text-white transition-colors text-left"
                                  >
                                    <span className="text-base">{countryCodeToFlag(c.code)}</span>
                                    {c.name}
                                  </button>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Squad level */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-zinc-400">{t("coachSignup.squadLevelLabel")}</label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {SQUAD_LEVELS.map((lvl) => {
                            const sel = squadLevel === lvl;
                            return (
                              <motion.button
                                key={lvl}
                                type="button"
                                onClick={() => setSquadLevel(lvl)}
                                className={`rounded-xl border-2 py-2.5 text-sm font-medium transition-all ${sel ? "border-violet-500 bg-violet-500/15 text-white" : "border-white/8 bg-white/3 text-zinc-400 hover:border-white/15"}`}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.97 }}
                              >
                                {t(`coachSignup.squad.${lvl}`)}
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* STEP 8 — Club history */}
            {stepId === "history" && (
              <motion.div key="history" className="space-y-6" {...motionProps}>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-1">{t("coachSignup.historyTitle")}</h2>
                  <p className="text-zinc-400 text-sm">{t("coachSignup.historySub")}</p>
                </div>
                <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1 team-list-scroll">
                  {teamHistory.map((entry, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-xl border border-white/10 bg-white/3 p-4 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t("coachSignup.clubN")} {i + 1}</span>
                        {teamHistory.length > 1 && (
                          <button type="button" onClick={() => removeHistory(i)} className="text-zinc-600 hover:text-red-400 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={entry.club}
                          onChange={(e) => updateHistory(i, "club", e.target.value)}
                          placeholder={t("signup.clubName")}
                          className="col-span-2 w-full rounded-xl border border-white/10 bg-white/5 py-2.5 px-3 text-sm text-white placeholder-zinc-500 focus:border-violet-500/50 outline-none transition-all"
                        />
                        <input
                          type="text"
                          value={entry.role}
                          onChange={(e) => updateHistory(i, "role", e.target.value)}
                          placeholder={t("coachSignup.rolePh")}
                          className="col-span-2 w-full rounded-xl border border-white/10 bg-white/5 py-2.5 px-3 text-sm text-white placeholder-zinc-500 focus:border-violet-500/50 outline-none transition-all"
                        />
                        <input
                          type="text"
                          value={entry.dateFrom}
                          onChange={(e) => updateHistory(i, "dateFrom", e.target.value)}
                          placeholder={t("signup.fromYear")}
                          maxLength={4}
                          className="w-full rounded-xl border border-white/10 bg-white/5 py-2.5 px-3 text-sm text-white placeholder-zinc-500 focus:border-violet-500/50 outline-none transition-all"
                        />
                        {entry.current ? (
                          <div className="flex items-center gap-2 rounded-xl border border-violet-500/30 bg-violet-500/10 px-3 py-2.5">
                            <span className="text-xs text-violet-400 font-medium">{t("signup.current")}</span>
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={entry.dateTo}
                            onChange={(e) => updateHistory(i, "dateTo", e.target.value)}
                            placeholder={t("signup.toYear")}
                            maxLength={4}
                            className="w-full rounded-xl border border-white/10 bg-white/5 py-2.5 px-3 text-sm text-white placeholder-zinc-500 focus:border-violet-500/50 outline-none transition-all"
                          />
                        )}
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={entry.current}
                          onChange={(e) => updateHistory(i, "current", e.target.checked)}
                          className="w-4 h-4 rounded accent-violet-500"
                        />
                        <span className="text-xs text-zinc-400">{t("coachSignup.currentlyHere")}</span>
                      </label>
                    </motion.div>
                  ))}
                </div>
                <motion.button
                  type="button"
                  onClick={addHistory}
                  className="flex items-center gap-2 text-sm text-violet-400 hover:text-violet-300 transition-colors"
                  whileHover={{ x: 2 }}
                >
                  <Plus className="w-4 h-4" /> {t("signup.addAnotherClub")}
                </motion.button>
              </motion.div>
            )}

            {/* STEP 9 — Photo */}
            {stepId === "photo" && (
              <motion.div key="photo" className="space-y-6" {...motionProps}>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-1">{t("signup.photoTitle")}</h2>
                  <p className="text-zinc-400 text-sm">{t("coachSignup.photoSub")}</p>
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setProfileImage(f); setProfilePreview(URL.createObjectURL(f)); }
                }} />
                <motion.button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full rounded-2xl border-2 border-dashed border-white/15 bg-white/3 hover:border-violet-500/40 hover:bg-violet-500/5 transition-all py-10 flex flex-col items-center gap-3"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  {profilePreview ? (
                    <img src={profilePreview} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-violet-500/40" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                      <Camera className="w-8 h-8 text-zinc-500" />
                    </div>
                  )}
                  <span className="text-sm text-zinc-400">{profilePreview ? t("coachSignup.tapToChange") : t("coachSignup.uploadPhoto")}</span>
                </motion.button>
              </motion.div>
            )}

            {/* STEP 10 — Account */}
            {stepId === "account" && (
              <motion.div key="account" className="space-y-6" {...motionProps}>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-1">{t("coachSignup.accountTitle")}</h2>
                  <p className="text-zinc-400 text-sm">{t("coachSignup.accountSub")}</p>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-zinc-400">{t("coachSignup.emailLabel")}</label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("coachSignup.emailPh")}
                        className="w-full rounded-xl border border-white/10 bg-white/5 py-3 pl-10 pr-4 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-zinc-400">{t("coachSignup.passwordLabel")}</label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type={showPass ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t("coachSignup.passwordPh")}
                        className="w-full rounded-xl border border-white/10 bg-white/5 py-3 pl-10 pr-12 text-white placeholder-zinc-500 focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
                      />
                      <button type="button" onClick={() => setShowPass((v) => !v)} aria-label={showPass ? t("coachSignup.hidePassword") : t("coachSignup.showPassword")} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
                        {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {/* Password strength */}
                    {password.length > 0 && (
                      <div className="flex gap-1 mt-1.5">
                        {[1,2,3,4].map((level) => {
                          const strength = Math.min(4, Math.floor(password.length / 3));
                          return (
                            <div key={level} className={`h-1 flex-1 rounded-full transition-colors ${level <= strength ? (strength <= 1 ? "bg-red-500" : strength <= 2 ? "bg-amber-500" : strength <= 3 ? "bg-yellow-400" : "bg-emerald-500") : "bg-white/10"}`} />
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-zinc-400">{t("signup.confirmPassword")}</label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type={showPass ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t("coachSignup.confirmPh")}
                        className={`w-full rounded-xl border py-3 pl-10 pr-10 text-white placeholder-zinc-500 focus:ring-2 outline-none transition-all bg-white/5 ${confirmPassword && confirmPassword !== password ? "border-red-500/60 focus:ring-red-500/20" : confirmPassword && confirmPassword === password ? "border-emerald-500/50 focus:ring-emerald-500/20 focus:border-emerald-500/50" : "border-white/10 focus:border-violet-500/50 focus:ring-violet-500/20"}`}
                      />
                      {confirmPassword && (
                        <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                          {confirmPassword === password
                            ? <Check className="w-4 h-4 text-emerald-400" />
                            : <span className="text-xs text-red-400">✗</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-zinc-500">
                  {t("coachSignup.byCreating")}{" "}
                  <Link href="/terms" className="text-violet-400 hover:text-violet-300 transition-colors">{t("coachSignup.termsOfService")}</Link>
                  {" "}{t("coachSignup.and")}{" "}
                  <Link href="/privacy" className="text-violet-400 hover:text-violet-300 transition-colors">{t("coachSignup.privacyPolicy")}</Link>.
                </p>
              </motion.div>
            )}

          </AnimatePresence>

          {/* Why Create account failed (email in use, weak password…). It was
              set but never shown, so the button looked like it did nothing —
              the bug fixed on the player page in July (6d8c7e931). */}
          {error && (
            <div className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300" role="alert">
              {error}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="mt-8 flex items-center gap-3">
            {step > 0 && (
              <motion.button
                type="button"
                onClick={goBack}
                className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-zinc-300 hover:bg-white/10 hover:text-white transition-all"
                whileHover={{ scale: 1.02, x: -1 }}
                whileTap={{ scale: 0.97 }}
              >
                <ChevronLeft className="w-4 h-4" /> {t("signup.back")}
              </motion.button>
            )}
            <motion.button
              type="button"
              onClick={isLast ? handleSubmit : goNext}
              disabled={!canNext() || submitting}
              className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold transition-all ${canNext() && !submitting ? "bg-gradient-to-r from-violet-500 to-violet-600 text-white shadow-[0_0_24px_rgba(139,92,246,0.35)] hover:from-violet-400 hover:to-violet-500" : "bg-white/5 text-zinc-600 cursor-not-allowed"}`}
              whileHover={canNext() && !submitting ? { scale: 1.01 } : {}}
              whileTap={canNext() && !submitting ? { scale: 0.98 } : {}}
            >
              {submitting ? (
                <motion.div
                  className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                />
              ) : isLast ? (
                <><Check className="w-4 h-4" /> {t("coachSignup.createCoachAccount")}</>
              ) : (
                <>{t("coachSignup.continue")} <ChevronRight className="w-4 h-4" /></>
              )}
            </motion.button>
          </div>

          {/* Already have account */}
          <p className="mt-5 text-center text-xs text-zinc-600">
            {t("coachSignup.alreadyHave")}{" "}
            <Link href="/signin" className="text-violet-400 hover:text-violet-300 transition-colors font-medium">{t("signup.signIn")}</Link>
            {" · "}
            <Link href="/signup" className="text-zinc-500 hover:text-zinc-400 transition-colors">{t("coachSignup.signUpAsPlayer")}</Link>
          </p>
        </div>

        {/* ── RIGHT: Visual panel ── */}
        <div className="hidden lg:flex flex-col items-center justify-center min-h-[400px]">
          <AnimatePresence mode="wait">

            {/* Role → Animated tactical board */}
            {stepId === "role" && (
              <motion.div key="v-role" className="w-full max-w-md" {...slideRight}>
                <div className="relative rounded-2xl border border-white/10 bg-black/40 p-6 overflow-hidden">
                  {/* Pitch SVG */}
                  <svg viewBox="0 0 100 100" className="w-full h-auto max-h-[300px] block mx-auto" style={{ filter: "drop-shadow(0 0 30px rgba(34,197,94,0.15))" }}>
                    <defs>
                      <linearGradient id="cpg" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#0d2818" />
                        <stop offset="50%" stopColor="#14532d" />
                        <stop offset="100%" stopColor="#0d2818" />
                      </linearGradient>
                    </defs>
                    <rect width="100" height="100" fill="url(#cpg)" rx="2" />
                    <rect x="2" y="2" width="96" height="96" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.4" rx="2" />
                    <line x1="50" y1="2" x2="50" y2="98" stroke="rgba(255,255,255,0.12)" strokeWidth="0.35" />
                    <circle cx="50" cy="50" r="9" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.35" />
                    {/* Tactical dots with staggered animation */}
                    {TACTIC_DOTS.map((dot, i) => (
                      <motion.circle
                        key={i}
                        cx={dot.x}
                        cy={dot.y}
                        r="3.5"
                        fill={i === 0 ? "rgba(251,191,36,0.85)" : "rgba(139,92,246,0.85)"}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: i * 0.06, type: "spring", stiffness: 300, damping: 20 }}
                      />
                    ))}
                    {/* Animated passing lines */}
                    {[[3,6],[6,9],[9,8],[4,7],[7,10]].map(([a,b], i) => (
                      <motion.line
                        key={i}
                        x1={TACTIC_DOTS[a].x} y1={TACTIC_DOTS[a].y}
                        x2={TACTIC_DOTS[b].x} y2={TACTIC_DOTS[b].y}
                        stroke="rgba(139,92,246,0.35)"
                        strokeWidth="0.6"
                        strokeDasharray="3 2"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{ delay: 0.7 + i * 0.1, duration: 0.4 }}
                      />
                    ))}
                  </svg>
                  {/* Clipboard icon overlay */}
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-500/20 flex items-center justify-center">
                    <Clipboard className="w-5 h-5 text-violet-400" />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Name → Name card preview */}
            {stepId === "name" && (
              <motion.div key="v-name" className="w-full max-w-xs" {...slideRight}>
                <motion.div
                  className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-violet-500/10 to-indigo-500/5 p-6 text-center overflow-hidden"
                  animate={{ boxShadow: firstName ? "0 0 40px rgba(139,92,246,0.2)" : "none" }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-violet-600/5 to-transparent pointer-events-none" />
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500/30 to-indigo-500/20 border border-violet-500/20 flex items-center justify-center mx-auto mb-4">
                    <span className="font-display text-xl font-bold text-violet-300">
                      {firstName ? firstName[0].toUpperCase() : "?"}
                    </span>
                  </div>
                  <div className="font-display text-lg font-bold text-white">
                    {firstName || "First"} {lastName || "Last"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {coachingRole ? t(`coachSignup.role.${coachingRole}`) : t("coachSignup.vCoachFallback")}
                  </div>
                  <div className="mt-4 h-px bg-gradient-to-r from-transparent via-violet-500/30 to-transparent" />
                  <div className="mt-3 flex justify-center">
                    <span className="text-xs px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 font-medium">{t("coachSignup.vDraffteCoach")}</span>
                  </div>
                </motion.div>
              </motion.div>
            )}

            {/* DOB → Calendar */}
            {stepId === "dob" && (
              <motion.div key="v-dob" className="rounded-2xl border border-white/10 bg-white/5 p-6 flex flex-col items-center gap-3" {...slideRight}>
                <Calendar className="w-12 h-12 text-violet-400/80" />
                <span className="font-display text-xl font-semibold text-white">
                  {MONTHS[dob.month - 1]} {dob.day}, {dob.year}
                </span>
                <span className="text-sm text-zinc-500">
                  {t("coachSignup.vYearsOld").replace("{n}", String(new Date().getFullYear() - dob.year))}
                </span>
              </motion.div>
            )}

            {/* Nationality → Globe */}
            {stepId === "nationality" && (
              <motion.div key="v-nat" className="flex flex-col items-center gap-4" {...slideRight}>
                <motion.div
                  className="w-32 h-32 rounded-full border-2 border-violet-500/20 bg-white/3 flex items-center justify-center"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <Globe className="w-14 h-14 text-violet-400/50" />
                </motion.div>
                {nationality && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center"
                  >
                    <div className="text-5xl mb-2">{countryCodeToFlag(nationality)}</div>
                    <div className="font-display text-lg font-semibold text-white">
                      {COUNTRIES.find((c) => c.code === nationality)?.name}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* License → Badge */}
            {stepId === "license" && (
              <motion.div key="v-license" className="flex flex-col items-center gap-6" {...slideRight}>
                <motion.div
                  className="relative w-40 h-40 flex items-center justify-center"
                  animate={licenseRank > 0 ? { scale: [1, 1.03, 1] } : {}}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {/* Glow ring */}
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    animate={licenseRank > 0 ? {
                      boxShadow: licenseRank === 5
                        ? ["0 0 30px rgba(251,191,36,0.2)", "0 0 60px rgba(251,191,36,0.5)", "0 0 30px rgba(251,191,36,0.2)"]
                        : ["0 0 20px rgba(139,92,246,0.2)", "0 0 50px rgba(139,92,246,0.5)", "0 0 20px rgba(139,92,246,0.2)"],
                    } : {}}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <div className={`w-32 h-32 rounded-full border-4 flex flex-col items-center justify-center gap-1 ${licenseRank === 5 ? "border-amber-400/60 bg-amber-400/10" : licenseRank > 0 ? "border-violet-500/60 bg-violet-500/10" : "border-white/10 bg-white/3"}`}>
                    <Shield className={`w-12 h-12 ${licenseRank === 5 ? "text-amber-400" : licenseRank > 0 ? "text-violet-400" : "text-zinc-600"}`} />
                    {license && (
                      <span className={`text-xs font-bold ${licenseRank === 5 ? "text-amber-400" : "text-violet-300"}`}>
                        {["", "GRS", "C", "B", "A", "PRO"][licenseRank]}
                      </span>
                    )}
                  </div>
                </motion.div>
                {license && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center text-sm text-zinc-400"
                  >
                    {t(`coachSignup.license.${license}`)}
                  </motion.p>
                )}
              </motion.div>
            )}

            {/* Experience → Timeline bar */}
            {stepId === "experience" && (
              <motion.div key="v-exp" className="w-full max-w-xs space-y-6" {...slideRight}>
                <div className="rounded-2xl border border-white/10 bg-white/3 p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-400">{t("coachSignup.vYearsCoaching")}</span>
                    <span className="font-display text-2xl font-bold text-white">{yearsExp === 30 ? "30+" : yearsExp}</span>
                  </div>
                  {/* Animated bar */}
                  <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-violet-600 to-violet-400"
                      animate={{ width: `${(yearsExp / 30) * 100}%` }}
                      transition={{ type: "spring", stiffness: 120, damping: 20 }}
                    />
                  </div>
                  {/* Milestones */}
                  <div className="flex justify-between text-xs text-zinc-600">
                    <span>{t("coachSignup.vBeginner")}</span><span>{t("coachSignup.vExperienced")}</span><span>{t("coachSignup.vVeteran")}</span>
                  </div>
                </div>
                {ageGroups.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-4"
                  >
                    <p className="text-xs text-violet-400 font-semibold mb-2">{t("coachSignup.vAgeGroupsSelected")}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {ageGroups.map((ag) => (
                        <span key={ag} className="text-xs px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/20">
                          {t(`coachSignup.age.${ag}`)}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Current club → Card */}
            {stepId === "club" && (
              <motion.div key="v-club" className="w-full max-w-xs" {...slideRight}>
                <AnimatePresence mode="wait">
                  {hasClub === true && clubName ? (
                    <motion.div
                      key="club-card"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="rounded-2xl border border-violet-500/20 bg-violet-500/5 p-5 text-center"
                    >
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500/30 to-indigo-500/20 border border-violet-500/20 flex items-center justify-center mx-auto mb-3">
                        <Trophy className="w-7 h-7 text-violet-400" />
                      </div>
                      <div className="font-display text-base font-bold text-white">{clubName || "–"}</div>
                      {clubCountry && <div className="text-sm text-zinc-400 mt-1">{countryCodeToFlag(clubCountry)} {COUNTRIES.find(c => c.code === clubCountry)?.name}</div>}
                      {squadLevel && <div className="mt-2 text-xs px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 inline-block">{t(`coachSignup.squad.${squadLevel}`)}</div>}
                    </motion.div>
                  ) : (
                    <motion.div key="club-placeholder" className="rounded-2xl border border-white/10 bg-white/3 p-6 flex flex-col items-center gap-3 text-center">
                      <Users className="w-12 h-12 text-zinc-600" />
                      <p className="text-sm text-zinc-500">{t("coachSignup.vClubPlaceholder")}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* History → Timeline */}
            {stepId === "history" && (
              <motion.div key="v-history" className="w-full max-w-xs space-y-2" {...slideRight}>
                {teamHistory.filter((e) => e.club).slice(0, 4).map((entry, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08 }}
                    className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/3 px-4 py-3"
                  >
                    <div className={`w-2 h-2 rounded-full shrink-0 ${entry.current ? "bg-emerald-400" : "bg-zinc-600"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{entry.club}</p>
                      <p className="text-xs text-zinc-500">{entry.dateFrom}{entry.dateTo ? ` – ${entry.dateTo}` : entry.current ? ` – ${t("signup.present")}` : ""}</p>
                    </div>
                  </motion.div>
                ))}
                {teamHistory.filter((e) => e.club).length === 0 && (
                  <div className="text-center text-sm text-zinc-600 py-8">{t("coachSignup.vHistoryPlaceholder")}</div>
                )}
              </motion.div>
            )}

            {/* Photo → Preview */}
            {stepId === "photo" && (
              <motion.div key="v-photo" className="flex flex-col items-center gap-4" {...slideRight}>
                <motion.div
                  className="w-36 h-36 rounded-full overflow-hidden border-2 border-violet-500/30 shadow-[0_0_40px_rgba(139,92,246,0.2)]"
                  animate={{ boxShadow: profilePreview ? "0 0 50px rgba(139,92,246,0.35)" : "0 0 20px rgba(139,92,246,0.1)" }}
                >
                  {profilePreview ? (
                    <img src={profilePreview} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-white/5 flex items-center justify-center">
                      <Camera className="w-12 h-12 text-zinc-600" />
                    </div>
                  )}
                </motion.div>
                {profilePreview && (
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-zinc-400">
                    {firstName} {lastName}
                  </motion.p>
                )}
              </motion.div>
            )}

            {/* Account → Lock */}
            {stepId === "account" && (
              <motion.div key="v-account" className="flex flex-col items-center gap-5" {...slideRight}>
                <motion.div
                  className="w-28 h-28 rounded-2xl bg-gradient-to-br from-violet-500/15 to-indigo-500/10 border border-violet-500/20 flex items-center justify-center"
                  animate={{ boxShadow: email && password.length >= 8 ? "0 0 40px rgba(139,92,246,0.25)" : "none" }}
                >
                  <Lock className="w-14 h-14 text-violet-400" />
                </motion.div>
                {email && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="text-center">
                    <p className="text-xs text-zinc-500">{t("coachSignup.vSigningUpAs")}</p>
                    <p className="text-sm font-medium text-white mt-0.5">{email}</p>
                  </motion.div>
                )}
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
